/**
 * The feedback ANCHOR PAYLOAD: one picked `Element` in, one submission-ready
 * anchor out (Issue #13577, epic #13573).
 *
 * ## Why this is a module and not three lines inside the picker
 *
 * It is the seam between two vocabularies that do not match, and both
 * mismatches fail SILENTLY from the client's point of view — the server parse
 * rejects the anchor and the submitter is told nothing useful:
 *
 *  - `SelectorPath.kind` (#13575) is lowercase and spells the attribute rung
 *    `'attribute'`. `FeedbackAnchorKind` (#13574) is uppercase and spells the
 *    same rung `ATTR`. {@link toFeedbackAnchorKind} is the whole translation
 *    and it is exhaustive by construction (a `Record`, not a `switch` with a
 *    default), so a sixth rung added on either side is a type error here
 *    rather than a rejected submission in production.
 *  - `getBoundingClientRect()` returns FLOATS, and returns NEGATIVE `x`/`y`
 *    for an element scrolled partly off the top or the left of the viewport.
 *    `feedbackAnchorPinRectSchema` takes NON-NEGATIVE INTEGERS, with `vw`/`vh`
 *    strictly POSITIVE. {@link toFeedbackAnchorPinRect} rounds, intersects
 *    with the viewport, and returns `null` — a value the schema accepts —
 *    rather than emitting a rect the server will refuse.
 *
 * ## Four length bounds, enforced here because they are enforced there
 *
 * {@link normalizeFeedbackAnchor} is the single place every bound is applied.
 * The label, the route and the surface name are TRUNCATED: they are display
 * strings, so a shortened one is degraded but still true. The selector is
 * NOT truncated — a truncated CSS selector is still a valid selector, it just
 * matches a different element, so an over-long path is replaced wholesale by
 * `UNRESOLVABLE_PATH`, whose entire contract is that it resolves to `null`.
 * The kind and the fragility follow the path down, or a caller would read
 * `ID` / `fragile: false` off a pointer that resolves to nothing.
 *
 * ## THE #13574 SWAP — read this before editing the type block below
 *
 * The anchor vocabulary — `NormalizedFeedbackAnchor`, `FeedbackAnchorKind`,
 * `FeedbackAnchorPinRect` and the four `*_MAX_LENGTH` bounds — belongs to
 * `src/lib/feedback/anchors.ts` (Issue #13574), which is the module
 * `submitFeedback` validates against. This file re-exports rather than
 * redeclares, so the shape the picker builds and the shape the server parses
 * cannot drift: a change to the server's contract is a type error here.
 *
 * DEPENDENCY-FREE OF REACT ON PURPOSE: the imports are the sibling
 * selector-path module and the type-level anchor contract, so this stays a
 * pure unit the picker can be tested without.
 */

import { computeSelectorPath, type SelectorPathKind } from './selector-path'
import { UNRESOLVABLE_PATH } from './selector-path.constants'
import {
  FEEDBACK_ANCHOR_KINDS,
  FEEDBACK_ANCHOR_LABEL_MAX_LENGTH,
  FEEDBACK_ANCHOR_ROUTE_MAX_LENGTH,
  FEEDBACK_ANCHOR_SELECTOR_MAX_LENGTH,
  FEEDBACK_ANCHOR_SURFACE_NAME_MAX_LENGTH,
  type FeedbackAnchorKind,
  type FeedbackAnchorPinRect,
  type NormalizedFeedbackAnchor,
} from './anchors'

// Re-exported so the picker and its tests keep one import site for the whole
// anchor vocabulary; `./anchors` remains the single declaration.
export {
  FEEDBACK_ANCHOR_KINDS,
  FEEDBACK_ANCHOR_LABEL_MAX_LENGTH,
  FEEDBACK_ANCHOR_ROUTE_MAX_LENGTH,
  FEEDBACK_ANCHOR_SELECTOR_MAX_LENGTH,
  FEEDBACK_ANCHOR_SURFACE_NAME_MAX_LENGTH,
  type FeedbackAnchorKind,
  type FeedbackAnchorPinRect,
  type NormalizedFeedbackAnchor,
}

/** The attribute this app uses to name a rendered surface (see ADR-0030). */
const SURFACE_ATTRIBUTE = 'data-surface'

/** The route a payload falls back to when the caller has none. */
const FALLBACK_ROUTE = '/'

/**
 * A `DOMRect`-shaped read, narrowed to the four edges this module uses.
 *
 * Structural rather than `DOMRect` so the picker can pass a plain object and
 * a test can exercise the rounding rules without a layout engine — happy-dom
 * reports every rect as zero, which would make every pin assertion vacuous.
 */
export interface AnchorRectLike {
  left: number
  top: number
  right: number
  bottom: number
}

/** The viewport a pin is expressed relative to. */
export interface AnchorViewportLike {
  width: number
  height: number
}

/**
 * The rung vocabulary translation, exhaustive by construction.
 *
 * A `Record<SelectorPathKind, FeedbackAnchorKind>` rather than a `switch`
 * with a default: a rung added to #13575 fails to compile here instead of
 * falling through to a plausible-looking `STRUCTURAL` that the server would
 * accept and that would quietly misreport how much the anchor can be trusted.
 */
const KIND_BY_SELECTOR_RUNG: Record<SelectorPathKind, FeedbackAnchorKind> = {
  id: 'ID',
  testid: 'TESTID',
  attribute: 'ATTR',
  text: 'TEXT',
  structural: 'STRUCTURAL',
}

/** `SelectorPath.kind` → `FeedbackAnchorKind`. Note `attribute` → `ATTR`. */
export function toFeedbackAnchorKind(
  kind: SelectorPathKind,
): FeedbackAnchorKind {
  return KIND_BY_SELECTOR_RUNG[kind]
}

function isPositiveSize(value: number): boolean {
  return Number.isFinite(value) && value > 0
}

/**
 * A measured rect as the non-negative integers the server accepts, or `null`.
 *
 * `null` is a real answer the schema takes, and it is the honest one in every
 * case below: a rect with no overlap with the viewport, a zero-area rect, a
 * non-finite coordinate (an unlaid-out element), or a viewport with no
 * positive size (`vw`/`vh` are strictly positive server-side). Clamping those
 * to `{0,0,0,0}` would instead draw a marker in the top-left corner of a page
 * the element was never in.
 *
 * The returned box is the INTERSECTION with the viewport, which is what makes
 * every component non-negative and in range without adjusting one edge and
 * leaving the opposite one describing a different rectangle.
 */
export function toFeedbackAnchorPinRect(
  rect: AnchorRectLike,
  viewport: AnchorViewportLike,
): FeedbackAnchorPinRect | null {
  const vw = Math.round(viewport.width)
  const vh = Math.round(viewport.height)
  if (!isPositiveSize(vw) || !isPositiveSize(vh)) return null

  const edges = [rect.left, rect.top, rect.right, rect.bottom]
  if (!edges.every(Number.isFinite)) return null

  const x = Math.max(0, Math.round(rect.left))
  const y = Math.max(0, Math.round(rect.top))
  const right = Math.min(vw, Math.round(rect.right))
  const bottom = Math.min(vh, Math.round(rect.bottom))
  const w = right - x
  const h = bottom - y
  if (w <= 0 || h <= 0) return null

  return { x, y, w, h, vw, vh }
}

/** Trims, truncates to `max`, and reports "nothing left" as `null`. */
function boundedText(value: string | null, max: number): string | null {
  if (value === null) return null
  const trimmed = value.trim()
  if (trimmed.length === 0) return null
  return trimmed.length > max ? trimmed.slice(0, max) : trimmed
}

/**
 * Applies every server-side bound to an already-assembled anchor.
 *
 * Separate from {@link buildFeedbackAnchor} because the bounds are the part
 * that has to hold for inputs the DOM cannot currently produce: #13575's
 * rungs cap an attribute value at 100 characters and a label at 60, so the
 * label and surface-name truncations are unreachable through a real element
 * today and would be untestable — and therefore untested — if they only
 * existed inside the DOM-reading path.
 */
export function normalizeFeedbackAnchor(
  input: NormalizedFeedbackAnchor,
): NormalizedFeedbackAnchor {
  const tooLong =
    input.selectorPath.length > FEEDBACK_ANCHOR_SELECTOR_MAX_LENGTH

  return {
    selectorPath: tooLong ? UNRESOLVABLE_PATH : input.selectorPath,
    anchorKind: tooLong ? 'STRUCTURAL' : input.anchorKind,
    fragile: tooLong ? true : input.fragile,
    pinRect: input.pinRect,
    route:
      boundedText(input.route, FEEDBACK_ANCHOR_ROUTE_MAX_LENGTH) ??
      FALLBACK_ROUTE,
    label: boundedText(input.label, FEEDBACK_ANCHOR_LABEL_MAX_LENGTH),
    surfaceName: boundedText(
      input.surfaceName,
      FEEDBACK_ANCHOR_SURFACE_NAME_MAX_LENGTH,
    ),
  }
}

/** What the picker knows about the moment of the pick. */
export interface FeedbackAnchorContext {
  /** The path the pick happened on. */
  route: string
  /** The element's measured rect, or `null` when none was measured. */
  rect: AnchorRectLike | null
  /** The viewport the rect was measured against. */
  viewport: AnchorViewportLike
}

/**
 * The anchor payload for one picked element.
 *
 * Total: it never throws and never returns null. An element that cannot be
 * addressed comes back carrying `UNRESOLVABLE_PATH`, which is a real answer —
 * the submitter learns at capture time that the pointer is no good instead of
 * finding out later by resolving to the wrong element.
 */
export function buildFeedbackAnchor(
  element: Element,
  context: FeedbackAnchorContext,
): NormalizedFeedbackAnchor {
  const selector = computeSelectorPath(element)
  const surface = element.closest(`[${SURFACE_ATTRIBUTE}]`)

  return normalizeFeedbackAnchor({
    selectorPath: selector.path,
    anchorKind: toFeedbackAnchorKind(selector.kind),
    fragile: selector.fragile,
    pinRect: context.rect
      ? toFeedbackAnchorPinRect(context.rect, context.viewport)
      : null,
    route: context.route,
    label: selector.label,
    surfaceName: surface?.getAttribute(SURFACE_ATTRIBUTE) ?? null,
  })
}
